import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle, ShieldCheck, GraduationCap, ClipboardCheck, Award, BookOpen,
  Home as HomeIcon, Utensils, ChevronDown, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { faqCategories } from '../data/faqs';
import SEO from '../components/SEO';
import { openInquiryPopup } from '../lib/contact';

const iconMap = { ShieldCheck, GraduationCap, ClipboardCheck, Award, BookOpen, HomeIcon, Utensils };

export default function Faqs() {
  const [active, setActive] = useState(faqCategories[0].id);
  const current = faqCategories.find((c) => c.id === active);

  return (
    <div className="bg-white" data-testid="faqs-page">
      <SEO
        title="FAQs — MBBS in Russia | Safety, NMC 2021, Hostels, Indian Food"
        description="27+ answers on MBBS in Russia — safety, degree recognition, NMC 2021 compatibility, internship, English medium, hostels & Indian food. Vetted by our senior counsellors."
        path="/faqs"
        keywords="MBBS in Russia FAQ, is Russia safe for Indian students, MBBS Russia hostel, Indian food Russia, NMC 2021 Russia compliance"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative">
          <div className="text-white max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <img src="/logo.jpg" alt="Russian Education" className="w-16 h-16 rounded-full border-2 border-white/40 bg-white p-1" />
              <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">
                frequently asked questions<br />mbbs in russia
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">
              FAQs — MBBS<br />in RUSSIA
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/90 max-w-3xl leading-relaxed">
              Everything Indian aspirants and parents ask us — from safety and NMC compliance to
              hostels, Indian food and clinical rotations. Answers vetted by our senior counsellors.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={openInquiryPopup} data-testid="faqs-talk-btn" className="bg-brand hover:bg-brand-dark text-white font-bold h-12 px-8 rounded-full btn-shift">
                Talk to a Counsellor
              </Button>
              <Link to="/universities" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/10 border border-white/30 text-white font-bold hover:bg-white/20 transition-colors">
                See Universities <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="py-10 md:py-14">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">Browse by Topic</div>
          <h2 className="mt-2 font-serif text-2xl md:text-4xl font-bold text-primary">Pick a Category</h2>

          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="faq-category-tabs">
            {faqCategories.map((c) => {
              const Icon = iconMap[c.icon] || HelpCircle;
              const isActive = active === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={`faq-tab-${c.id}`}
                  className={`inline-flex items-center gap-2 h-10 px-4 rounded-full border text-sm font-semibold transition-all
                    ${isActive
                      ? 'bg-brand text-white border-brand shadow-md shadow-brand/30'
                      : 'bg-white text-primary border-border hover:border-brand/50 hover:text-brand'}`}
                >
                  <Icon className="w-4 h-4" />
                  {c.title}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Active category FAQs */}
      <section className="pb-4">
        <div className="container mx-auto max-w-4xl px-4 md:px-8">
          <div className="flex items-center gap-3 mb-6" data-testid="faq-active-heading">
            {(() => {
              const Icon = iconMap[current.icon] || HelpCircle;
              return (
                <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
              );
            })()}
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">{current.title}</h3>
          </div>

          <div className="space-y-3">
            {current.faqs.map((item, i) => (
              <details
                key={`${current.id}-${i}`}
                className="group bg-white rounded-xl border border-border shadow-sm hover:shadow-md hover:border-brand/40 transition-all"
                data-testid={`faq-item-${current.id}-${i}`}
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 md:p-6 select-none">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-brand text-white text-sm font-bold flex items-center justify-center mt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <h4 className="font-serif text-base md:text-lg font-bold text-primary leading-snug">
                      {item.q}
                    </h4>
                  </div>
                  <div className="shrink-0 w-9 h-9 rounded-full bg-muted flex items-center justify-center transition-transform duration-300 group-open:rotate-180 group-open:bg-brand group-open:text-white">
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </summary>
                <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1">
                  <div className="pl-11 text-sm md:text-base text-muted-foreground leading-relaxed">
                    {item.a}
                  </div>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-primary text-white p-8 md:p-10">
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 15c-6 6-4 14 3 17 6 3 15-2 15-10 0-6-6-12-13-12-2 0-4 2-5 5zm0 30c-14 8-26 25-26 42 0 12 12 24 26 24s26-12 26-24c0-17-12-34-26-42z'/%3E%3C/svg%3E\")", backgroundSize: '120px' }} />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold mb-2">Still Have a Question?</div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold leading-tight">
                  Not finding your answer? Talk to a senior counsellor and get personalised guidance in minutes.
                </h3>
              </div>
              <a href="tel:+919509406802" className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold h-12 px-7 rounded-full btn-shift shrink-0" data-testid="faq-call-cta">
                Call +91 95094 06802
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
